import React from 'react';
import { useNavigate } from 'react-router-dom';

import { getSupabaseClient } from '@/api/supabaseClient';
import { parseAuthCallbackUrl } from '@/lib/authRecovery';
import { createPageUrl } from '@/utils';

const AUTH_DEBUG_STORAGE_KEY = 'mbv-last-auth-debug';

function isNativePlatform() {
  if (typeof window === 'undefined') return false;
  if (typeof window.Capacitor?.isNativePlatform === 'function') {
    return window.Capacitor.isNativePlatform();
  }
  return false;
}

function getCapacitorAppPlugin() {
  if (typeof window === 'undefined') return null;
  return window.Capacitor?.Plugins?.App || null;
}

export default function AuthDeepLinkManager() {
  const navigate = useNavigate();
  const handledUrlsRef = React.useRef(new Set());

  React.useEffect(() => {
    if (!isNativePlatform()) return undefined;

    const appPlugin = getCapacitorAppPlugin();
    if (!appPlugin?.addListener) return undefined;

    const storageKey = 'mbv-handled-auth-callback-urls';
    const writeAuthDebug = (payload) => {
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(
          AUTH_DEBUG_STORAGE_KEY,
          JSON.stringify({
            timestamp: new Date().toISOString(),
            ...payload,
          }),
        );
      } catch (_error) {
        // Ignore debug persistence failures.
      }
    };

    const loadHandledUrls = () => {
      if (typeof window === 'undefined') return [];
      try {
        const rawValue = window.sessionStorage.getItem(storageKey);
        const parsedValue = rawValue ? JSON.parse(rawValue) : [];
        return Array.isArray(parsedValue) ? parsedValue : [];
      } catch (_error) {
        return [];
      }
    };

    const persistHandledUrl = (rawUrl) => {
      if (typeof window === 'undefined') return;
      const nextUrls = Array.from(handledUrlsRef.current);
      try {
        window.sessionStorage.setItem(storageKey, JSON.stringify(nextUrls.slice(-10)));
      } catch (_error) {
        // Ignore storage issues; in-memory dedupe still prevents loops during this mount.
      }
    };

    handledUrlsRef.current = new Set(loadHandledUrls());

    const handleIncomingUrl = async (rawUrl) => {
      const navigateAppLink = () => {
        try {
          const parsedUrl = new URL(rawUrl);
          const isRankingLink = (
            parsedUrl.protocol === 'com.mbv.igreja:' &&
            ['ranking-mbv', 'rankingmbv'].includes(parsedUrl.host.toLowerCase())
          ) || (
            parsedUrl.hostname === 'www.igrejambv.com.br' &&
            parsedUrl.pathname.toLowerCase() === '/rankingmbv'
          );

          if (!isRankingLink) return false;

          const token = parsedUrl.searchParams.get('checkin') || parsedUrl.searchParams.get('token') || '';
          const target = token
            ? `${createPageUrl('RankingMBV')}?checkin=${encodeURIComponent(token)}`
            : createPageUrl('RankingMBV');
          navigate(target, { replace: true });
          return true;
        } catch (_error) {
          return false;
        }
      };

      if (navigateAppLink()) return;

      const callbackData = parseAuthCallbackUrl(rawUrl);
      if (!callbackData?.isAuthCallback) return;
      if (handledUrlsRef.current.has(rawUrl)) return;

      handledUrlsRef.current.add(rawUrl);
      persistHandledUrl(rawUrl);

      const notifyAuthCallback = (detail) => {
        if (typeof window === 'undefined') return;
        window.dispatchEvent(new CustomEvent('mbv-auth-callback', { detail }));
      };

      writeAuthDebug({
        stage: 'received',
        url: rawUrl,
        callbackData,
      });

      if (callbackData.error) {
        writeAuthDebug({
          stage: 'provider_error',
          url: rawUrl,
          callbackData,
        });
        notifyAuthCallback({
          success: false,
          error: callbackData.errorDescription || callbackData.error,
        });
        navigate(createPageUrl('Perfil'), { replace: true });
        return;
      }

      try {
        const supabase = getSupabaseClient();
        const browserPlugin = window.Capacitor?.Plugins?.Browser;
        let sessionResolved = false;

        if (callbackData.code) {
          const { error } = await supabase.auth.exchangeCodeForSession(callbackData.code);
          if (error) throw error;
          sessionResolved = true;
        } else if (callbackData.accessToken && callbackData.refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: callbackData.accessToken,
            refresh_token: callbackData.refreshToken,
          });
          if (error) throw error;
          sessionResolved = true;
        } else {
          const { data, error } = await supabase.auth.getSession();
          if (error) throw error;
          sessionResolved = Boolean(data?.session);
        }

        if (!sessionResolved) {
          throw new Error('Nao foi possivel concluir o login com Google. Tente novamente.');
        }

        if (browserPlugin?.close && window.Capacitor?.getPlatform?.() === 'ios') {
          await browserPlugin.close().catch(() => {});
        }
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        writeAuthDebug({
          stage: 'success',
          url: rawUrl,
          userId: userData?.user?.id || '',
        });
        notifyAuthCallback({ success: true });
      } catch (error) {
        console.error('[auth] falha ao concluir callback oauth', error);
        writeAuthDebug({
          stage: 'callback_error',
          url: rawUrl,
          message: error?.message || 'Nao foi possivel concluir o login social.',
          stack: error?.stack || '',
        });
        notifyAuthCallback({
          success: false,
          error: error?.message || 'Nao foi possivel concluir o login social.',
        });
      }

      if (callbackData.isRecovery) {
        try {
          navigate(`${createPageUrl('Perfil')}?mode=update`, { replace: true });
        } catch (_error) {
          navigate(createPageUrl('Perfil'), { replace: true });
        }
        return;
      }

      navigate(createPageUrl('Perfil'), { replace: true });
    };

    let listenerHandle = null;
    try {
      const addListenerResult = appPlugin.addListener('appUrlOpen', ({ url }) => {
        Promise.resolve(handleIncomingUrl(url)).catch((error) => {
          console.error('[auth] erro nao tratado em appUrlOpen', error);
          writeAuthDebug({
            stage: 'listener_error',
            url,
            message: error?.message || 'Erro inesperado ao abrir o link de autenticacao.',
            stack: error?.stack || '',
          });
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('mbv-auth-callback', {
              detail: {
                success: false,
                error: error?.message || 'Erro inesperado ao abrir o link de autenticacao.',
              },
            }));
          }
          navigate(createPageUrl('Perfil'), { replace: true });
        });
      });

      if (addListenerResult && typeof addListenerResult.then === 'function') {
        addListenerResult
          .then((handle) => {
            listenerHandle = handle;
          })
          .catch(() => {});
      } else {
        listenerHandle = addListenerResult || null;
      }
    } catch (_error) {
      listenerHandle = null;
    }

    if (typeof appPlugin.getLaunchUrl === 'function') {
      appPlugin.getLaunchUrl()
        .then((result) => {
          if (result?.url) {
            handleIncomingUrl(result.url);
          }
        })
        .catch(() => {});
    }

    return () => {
      listenerHandle?.remove?.();
    };
  }, [navigate]);

  return null;
}
